import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import Tooltip from 'common/common/Tooltip';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import LandingProductboardHelmet from 'common/helmets/LandingProductboardHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LandingLogos from 'common/LandingLogos';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import UppercaseHeader from 'common/UppercaseHeader';
import withContexts from 'common/util/withContexts';
import Anurag from 'img/landing/anurag.webp';
import GoogleLogo from 'img/landing/google-logo.webp';
import Ida from 'img/landing/ida.webp';
import MercuryWordmark from 'img/landing/mercury-wordmark.webp';
import MicrosoftTeamsLogo from 'img/landing/microsoft-teams-logo.webp';
import OktaLogo from 'img/landing/okta-logo.webp';
import ProductboardIcon from 'img/landing/pb-logo.webp';
import Sam from 'img/landing/sam.webp';
import SegmentLogo from 'img/landing/segment-logo.webp';
import Subdomain1x from 'img/landing/subdomain-1x.webp';
import Subdomain2x from 'img/landing/subdomain-2x.webp';

import CannyLogo from 'img/canny-wordmark-white.svg';
import GitHubLogo from 'img/landing/github-logo.svg';
import IntercomLogo from 'img/landing/intercom-logo.svg';
import JiraLogo from 'img/landing/jira-logo.svg';
import SalesforceLogo from 'img/landing/salesforce-logo.svg';
import SlackLogo from 'img/landing/slack-logo.svg';
import ZapierLogo from 'img/landing/zapier-logo.svg';
import ZendeskLogo from 'img/landing/zendesk-logo.svg';

import 'css/components/www/_LandingComparisonPageNew.scss';

class LandingProductboard extends Component {
  static propTypes = {
    showIntercom: PropTypes.func,
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Productboard Page');
  }

  render() {
    return (
      <div className="landingProductboard landingComparisonPage">
        <LandingProductboardHelmet />
        <div className="mainContentContainer">
          <div className="header">
            <div className="left">
              <h1>Canny is a powerful, focused alternative to&nbsp;Productboard</h1>
              <p className="text">
                Productboard is an overly complex tool with more customization options than you
                really need. Plus, their product is&nbsp;half-baked.
              </p>
              <p className="text">
                Canny is a flexible tool that’s both lightweight and powerful. It’s{' '}
                <Link to="/pricing">cost&#8209;effective</Link>, quick to set up, and prioritizes{' '}
                <Link to="/features">features you’ll actually&nbsp;use</Link>.
              </p>
              <Link className="cta" to="/register">
                <Button buttonType="greenButton" value="Get Started" />
              </Link>
            </div>
            <LazyLoadedImage
              className="headerImg"
              loading="eager"
              src={Subdomain1x}
              srcSet={`${Subdomain1x} 1x, ${Subdomain2x} 2x`}
              alt="A feedback board in Canny, a Productboard alternative, displaying users’ feature requests"
            />
          </div>
          <div className="section">
            <h2 className="sectionHeader">Table of&nbsp;contents</h2>
            <div className="sectionContent">
              <a href="#comparison" className="text toc-option">
                <p className="number">01</p>
                <p className="option-text">
                  Quick look: Why people prefer Canny over&nbsp;Productboard
                </p>
              </a>
              <a href="#ux" className="text toc-option">
                <p className="number">02</p>
                <p className="option-text">
                  Canny offers a better design and user experience than&nbsp;Productboard
                </p>
              </a>
              <a href="#data" className="text toc-option">
                <p className="number">03</p>
                <p className="option-text">
                  With Productboard, access to your data is&nbsp;limited
                </p>
              </a>
              <a href="#feedback" className="text toc-option">
                <p className="number">04</p>
                <p className="option-text">Canny was built specifically for user&nbsp;feedback</p>
              </a>
              <a href="#integrations" className="text toc-option">
                <p className="number">05</p>
                <p className="option-text">
                  Canny has more robust integrations than&nbsp;Productboard
                </p>
              </a>
              <a href="#costs" className="text toc-option">
                <p className="number">06</p>
                <p className="option-text">Your whole team can affordably use&nbsp;Canny</p>
              </a>
            </div>
          </div>
          <div className="section" id="comparison">
            <h2 className="sectionHeader">
              Quick look: Why people prefer Canny over&nbsp;Productboard
            </h2>
            <div className="sectionContent">
              <div className="comparisonChart">
                <div className="comparisonLogoHeader">
                  <div className="canny">
                    <LazyLoadedImage src={CannyLogo} alt="Canny logo" />
                  </div>
                  <div className="competitorLogo productboard">
                    <LazyLoadedImage src={ProductboardIcon} alt="ProductBoard Logo" />
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <p className="title">Only the features you&nbsp;need</p>
                    <p className="description">
                      We’ll integrate with your existing workflow and allow other tools to do
                      their&nbsp;jobs
                    </p>
                  </div>
                  <div className="point">
                    <p className="title">Feature bloated</p>
                    <p className="description">
                      Half the battle is figuring out which features you want to turn&nbsp;on
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <p className="title">Public API</p>
                    <p className="description">
                      Your data belongs to you. We won’t penalize you if you decide to&nbsp;switch.
                    </p>
                  </div>
                  <div className="point">
                    <p className="title">Limited access to data</p>
                    <p className="description">
                      Much harder to switch, export your data, and increased risk of losing
                      it&nbsp;all
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <p className="title">Feedback focused</p>
                    <p className="description">
                      Built from the ground up around feedback&nbsp;management.
                    </p>
                  </div>
                  <div className="point">
                    <p className="title">Feedback's an afterthought</p>
                    <p className="description">
                      Managing user feedback is manual. It's not built with feedback in&nbsp;mind.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <p className="title">Flexible pricing</p>
                    <p className="description">
                      We have plans that work for early startups through&nbsp;enterprise
                    </p>
                  </div>
                  <div className="point">
                    <p className="title">Pricing Issues</p>
                    <p className="description">
                      Pay hefty prices per user just to access basic&nbsp;features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section" id="ux">
            <h2 className="sectionHeader">
              Canny offers a better design and user experience than&nbsp;Productboard
            </h2>
            <div className="sectionContent">
              <p className="text">Productboard is a complicated tool that does too&nbsp;much.</p>
              <p className="text">
                They support just about every use-case you can imagine. It’s challenging to
                customize Productboard so that it actually works for you and makes your
                life&nbsp;easier.
              </p>
              <p className="text">
                It’s hard to find what you’re looking for. And, the customization options are more
                granular than most people really&nbsp;need.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  <p className="text">
                    "There are SO MANY features and the ability to basically customize any of them,
                    it can be a bit overwhelming to get&nbsp;started.
                  </p>
                  <p className="text">
                    The onboarding tutorial was pretty long in order to cover all the features, and
                    at the end I wasn't quite sure how I needed to configure things to best suit
                    my&nbsp;needs."
                  </p>
                </p>
                <div className="credit">
                  <p className="text">
                    <p className="title">
                      –{' '}
                      <a
                        href="https://www.capterra.com/p/160651/productboard/reviews/"
                        rel="noopener nofollow"
                        target="_blank">
                        Capterra review of Productboard
                      </a>
                    </p>
                  </p>
                </div>
              </div>
              <p className="text">
                We built Canny so that it’s straightforward and&nbsp;intuitive.
              </p>
              <p className="text">
                It’s a robust tool with everything you need. But, it’s not so overbuilt that you get
                lost in&nbsp;it.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  <p className="text">
                    "[Productboard] perhaps has too many features and options! The responsibility is
                    on the user to not over-complicate the&nbsp;implementation."
                  </p>
                </p>
                <div className="credit">
                  <p className="text">
                    <p className="title">
                      –{' '}
                      <a
                        href="https://www.capterra.com/p/160651/productboard/reviews/"
                        rel="noopener nofollow"
                        target="_blank">
                        Capterra review of&nbsp;Productboard
                      </a>
                    </p>
                  </p>
                </div>
              </div>
              <p className="text">
                In short, Canny allows for customization, without being too much. It guides you
                toward success, rather than complicating the process&nbsp;unnecessarily.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  "Liking Canny so far! [...] So much more dev-friendly than Productboard which is a
                  bit over-engineered in terms of&nbsp;UX."
                </p>
                <div className="credit">
                  <LazyLoadedImage src={Anurag} alt="Photo of Anurag Goel" />
                  <p className="text">
                    <p className="name">Anurag Goel</p>
                    <p className="title">
                      Founder and CEO at{' '}
                      <a href="https://render.com/" rel="noopener" target="_blank">
                        Render
                      </a>
                    </p>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section" id="data">
            <h2 className="sectionHeader">
              With Productboard, access to your data is&nbsp;limited
            </h2>
            <div className="sectionContent">
              <p className="text">
                Productboard only offers a public API on their most expensive plans. This is
                intentional, as it makes it harder to switch to another&nbsp;tool.
              </p>
              <div className="warning">
                <div className="heading">WARNING</div>
                <p className="text">
                  Lack of a public API is one of the biggest risks of going with&nbsp;Productboard.
                </p>
                <p className="text">
                  Without it, you can’t use your data elsewhere. This means, it's much harder to:
                </p>
                <ul>
                  <li>Switch to another tool</li>
                  <li>Pull data into another tool (eg. for analytics)</li>
                  <li>Build something custom into your UI</li>
                </ul>
              </div>
              <p className="text">
                Choosing a tool that “locks in” your data is inherently risky. You won’t be able to
                use the data you gather and input anywhere else. You can only access it
                within&nbsp;Productboard.
              </p>
              <p className="text">
                And, if you choose to try a new tool, it means a more difficult process with
                migrating your data (or losing access to&nbsp;everything).
              </p>
              <p className="text">
                It’s not user-friendly, and leaves you with limited&nbsp;options.
              </p>
              <p className="text">
                At Canny, we believe that you should have access to your data. We hope you’ll try
                Canny and stick around forever, but we won’t penalize you if you decide it’s not the
                right fit for your&nbsp;team.
              </p>
            </div>
          </div>
          <div className="section" id="feedback">
            <h2 className="sectionHeader">Canny was built specifically for user&nbsp;feedback</h2>
            <div className="sectionContent">
              <p className="text">
                Productboard is a product management tool first and foremost. They added on a user
                feedback feature after the fact, and it&nbsp;shows.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  <p className="text">
                    "Productboard's UI and organizational structure makes it a bit difficult to
                    understand who specifically gave you the&nbsp;feedback.
                  </p>
                  <p className="text">
                    It's a manual process to export users' insights so that our support team can
                    follow through on customer feedback once we've delivered on their&nbsp;asks."
                  </p>
                </p>
                <div className="credit">
                  <p className="text">
                    <p className="title">
                      –{' '}
                      <a
                        href="https://www.capterra.com/p/160651/productboard/reviews/"
                        rel="noopener nofollow"
                        target="_blank">
                        Capterra review of&nbsp;Productboard
                      </a>
                    </p>
                  </p>
                </div>
              </div>
              <p className="text">
                Overall, their feedback functionality isn’t very robust, and their users say it has{' '}
                <a
                  href="https://www.g2.com/products/productboard/reviews/productboard-review-3997758"
                  rel="noopener">
                  “a lot of space for&nbsp;improvement.”
                </a>
              </p>
              <p className="text">
                Productboard’s user feedback tool is essentially another inbox you’ll have to go
                through. Managing user feedback in Productboard means more manual effort,
                not&nbsp;less.
              </p>
              <p className="text">
                Productboard doesn’t place feedback and community at the center of product
                management. Canny&nbsp;does.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  <p className="text">
                    “Before Canny, we were using Productboard. We didn't like it because it was
                    lacking interaction with the community. So we started looking for a tool with a
                    public roadmap where the community could interact with us and also between each
                    other. It was really important for us – to give them a place where they can chat
                    about feature requests and what's missing in the&nbsp;product.”
                  </p>
                </p>
                <div className="credit">
                  <p className="text">
                    <div className="credit">
                      <p className="text">
                        <p className="name">Aurélien Georget</p>
                        <p className="title">
                          CPO at{' '}
                          <a
                            href="https://canny.io/case-studies/strapi"
                            rel="noopener"
                            target="_blank">
                            Strapi
                          </a>
                        </p>
                      </p>
                    </div>
                  </p>
                </div>
              </div>
              <p className="text">
                Canny’s user feedback tool is straightforward to use, plays well with your existing
                tool set, and streamlines&nbsp;processes.
              </p>
              <p className="text">
                We’ve simplified feedback management with features like user voting, similar post
                suggester, and the ability to merge&nbsp;posts.
              </p>
              <p className="text">
                Having an easy way to capture and manage user feedback is vital. You’ll be able to
                make informed product decisions—without a complicated manual&nbsp;process.
              </p>
            </div>
          </div>
          <div className="section" id="price">
            <h2 className="sectionHeader">
              Productboard's best feedback management features are&nbsp;pricey
            </h2>
            <div className="sectionContent">
              <p className="text">
                Productboard's product management software covers a lot of functions. But, many of
                them are undeveloped. Some are even gated behind expensive subscriptions. That
                includes many of their feedback management&nbsp;tools.
              </p>
              <p className="text">
                Want to manage a feedback board? Use a Chrome extension to collect feedback and
                ideas as you browse the web? Use integrations to streamline
                feedback&nbsp;collection?
              </p>
              <p className="text">Those are all gated behind their pro&nbsp;plan.</p>
              <p className="text">
                With Canny, those features are available on all our plans – even our Free&nbsp;plan.
              </p>
            </div>
          </div>
          <div className="section" id="integrations">
            <h2 className="sectionHeader">
              Canny has more robust integrations than&nbsp;Productboard
            </h2>
            <div className="sectionContent">
              <p className="text">
                Productboard has plenty of integrations. But, they’re not powerful enough to easily
                capture user feedback or work with your existing&nbsp;tools.
              </p>
              <p className="text">
                For example, Productboard boasts a Slack integration—but it doesn’t work as well
                with their feedback feature as it&nbsp;should:
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  <p className="text">
                    "It's quite complex to implement and you have [to] log into it daily to view
                    feedback from users as there is no Slack integration to surface these insights
                    outside of&nbsp;Productboard.
                  </p>
                  <p className="text">
                    They advertise a Slack integration but this just shares updates from internal
                    users and is useless in all&nbsp;honesty."
                  </p>
                </p>
                <div className="credit">
                  <p className="text">
                    <p className="title">
                      –{' '}
                      <a
                        href="https://www.g2.com/products/productboard/reviews/productboard-review-4337819"
                        rel="noopener"
                        target="_blank">
                        G2 review of&nbsp;Productboard
                      </a>
                    </p>
                  </p>
                </div>
              </div>
              <p className="text">
                Users have also noted that Productboard’s{' '}
                <a
                  href="https://www.g2.com/products/productboard/reviews/productboard-review-453515"
                  rel="noopener">
                  Zendesk integration isn’t very powerful
                </a>
                , and that{' '}
                <a
                  href="https://www.g2.com/survey_responses/productboard-review-441283"
                  rel="noopener">
                  “integration with Jira isn't as robust (2-way) or easy to use as I'd&nbsp;like.”
                </a>
              </p>
              <p className="text">
                We want using Canny to make things easier for you, not add unnecessary steps and
                complications. That's why we integrate with most major customer support, team
                collaboration, and project management&nbsp;tools.
              </p>
              <p className="text">
                <Link to="/integrations">
                  Canny integrates with Slack, Zendesk, Jira, Salesforce, and&nbsp;more
                </Link>
                .
              </p>
              <div className="integrationsComparison">
                <div className="comparisonLogoHeader connected">
                  <div className="canny solo">
                    <LazyLoadedImage src={CannyLogo} alt="Canny Logo" />
                  </div>
                </div>
                <div className="integrationsGrids">
                  <div className="integrationsGrid solo">
                    <div className="integrationsGridInner">
                      <Link to="/integrations/slack" className="integration slackImage">
                        <Tooltip position="top" value="Slack">
                          <LazyLoadedImage src={SlackLogo} alt="Slack logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/jira" className="integration jiraImage">
                        <Tooltip position="top" value="Jira">
                          <LazyLoadedImage src={JiraLogo} alt="Jira logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/intercom" className="integration intercomImage">
                        <Tooltip position="top" value="Intercom">
                          <LazyLoadedImage src={IntercomLogo} alt="Intercom logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://zapier.com/apps/canny/integrations"
                        rel="noopener"
                        className="integration zapierImage">
                        <Tooltip position="top" value="Zapier">
                          <LazyLoadedImage src={ZapierLogo} alt="Zapier logo" />
                        </Tooltip>
                      </a>
                      <Link to="/integrations/github" className="integration githubImage">
                        <Tooltip position="top" value="GitHub">
                          <LazyLoadedImage src={GitHubLogo} alt="GitHub logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/segment" className="integration segmentImage">
                        <Tooltip position="top" value="Segment">
                          <LazyLoadedImage src={SegmentLogo} alt="Segment logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://help.canny.io/en/articles/4343053-microsoft-teams-integration"
                        className="integration microsoftTeamsImage">
                        <Tooltip position="top" value="Microsoft Teams">
                          <LazyLoadedImage src={MicrosoftTeamsLogo} alt="Microsoft Teams logo" />
                        </Tooltip>
                      </a>
                      <Link to="/integrations/salesforce" className="integration salesforceImage">
                        <Tooltip position="bottom" value="Salesforce">
                          <LazyLoadedImage src={SalesforceLogo} alt="Salesforce logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/zendesk" className="integration zendeskImage">
                        <Tooltip position="bottom" value="Zendesk">
                          <LazyLoadedImage src={ZendeskLogo} alt="Zendesk logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://help.canny.io/en/articles/4029303-okta-sso-integration"
                        className="integration oktaImage">
                        <Tooltip position="bottom" value="Okta">
                          <LazyLoadedImage src={OktaLogo} alt="Okta logo" />
                        </Tooltip>
                      </a>
                      <a
                        href="https://help.canny.io/en/articles/4228880-g-suite-integration"
                        className="integration googleImage">
                        <Tooltip position="bottom" value="Google Workspace">
                          <LazyLoadedImage src={GoogleLogo} alt="Google logo" />
                        </Tooltip>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text">
                And, our integrations are robust and well-executed. It isn’t a manual process to log
                feedback you receive in Zendesk, for example—you won’t have to leave the&nbsp;tool.
              </p>
              <div className="quoteContainer">
                <p className="quote">
                  "I really like [Canny]! I like how easily it integrates into Intercom too. It
                  makes it very easy for customers to provide feedback, unlike other solutions like
                  Productboard where you have to modify your web app's code to embed the feature
                  request&nbsp;portal."
                </p>
                <div className="credit">
                  <LazyLoadedImage src={Sam} alt="Photo of Sam Hulick" />
                  <p className="text">
                    <p className="name">Sam Hulick</p>
                    <p className="title">
                      Founder and CEO at{' '}
                      <a href="https://reelcrafter.com/" rel="noopener" target="_blank">
                        ReelCrafter
                      </a>
                    </p>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section" id="costs">
            <h2 className="sectionHeader">Your whole team can affordably access&nbsp;Canny</h2>
            <div className="sectionContent">
              <p className="text">
                Need to give multiple team members access to manage your&nbsp;board?
              </p>
              <p className="text">
                With Productboard, you’ll have to pay a hefty price per&nbsp;seat.
              </p>
              <p className="text">
                They charge for each manager seat and offer no discounts as you&nbsp;grow.
              </p>
              <p className="text">
                Canny's plans include 5 manager seats. Extra managers can be added at an affordable
                price. You also get unlimited contributor seats for free. So, your whole team can
                easily and affordably&nbsp;collaborate.
              </p>
              <p className="text">
                Learn more about <Link to="/pricing">Canny’s pricing&nbsp;here</Link>.
              </p>
            </div>
          </div>
          <div className="section" id="customers">
            <h2 className="sectionHeader">Try Canny today</h2>
            <div className="sectionContent">
              <p className="text">
                Here at Canny, we have customers all the way from 2-person startups to billion
                dollar companies like <Link to="/case-studies">CircleCI&nbsp;and&nbsp;Mercury</Link>
                .
              </p>
              <Link className="customerItemContents mercury" to="/case-studies/mercury">
                <p className="text">
                  <div className="top">
                    <div className="wordmark">
                      <LazyLoadedImage src={MercuryWordmark} alt="Mercury logo" />
                    </div>
                    <p className="description">
                      How Mercury found a drastic improvement in efficiency and&nbsp;productivity
                    </p>
                  </div>
                  <UppercaseHeader>
                    Read their story <span className="icon-chevron-right" />
                  </UppercaseHeader>
                </p>
                <LazyLoadedImage className="profile" src={Ida} alt="Photo of Ida Ström" />
              </Link>
              <p className="text">
                Get started with Canny for free, or{' '}
                <Link to="/request-demo">reach out to us for a demo</Link> to see Canny in
                action.{' '}
              </p>
            </div>
          </div>
          <LandingLogos />
          <LandingFooterCTA style="solid" />
        </div>
      </div>
    );
  }
}

export default compose(
  withContexts({
    showIntercom: ShowIntercomContext,
    trackEvent: TrackEventContext,
  }),
  hot,
)(LandingProductboard);
